import { normalizeFreightStatusDescribe } from "../../view/frete-copy/action/action";
import {
  computeDueDateFromPaymentCondition,
  normalizeFreightIssueDate,
} from "./payment-due-date";
import {
  formatFinancialStatusValue,
  normalizeStoredFinancialStatusValue,
} from "./freight-v2-table-utils";

import { resolvePayingShipperUid } from "./resolvePayingShipper";

/** Mapeia o retorno do backend para o shape das tabelas de frete. */
export function mapFreightFromApi(item) {
  const id = item.id || item._id;

  const pagadorRaw = item.pagador ?? item.freight?.pagador;
  const pagadorUid =
    typeof pagadorRaw === "string"
      ? pagadorRaw
      : pagadorRaw?.uid ?? pagadorRaw?.id ?? "";

  const payingUid = resolvePayingShipperUid({
    ...item,
    pagador: pagadorUid || pagadorRaw,
  });

  const clientPayment = {
    uid: payingUid || item.clientPayment?.uid || item.client?.uid || "",
    name: item.clientPayment?.name || item.client?.name || "",
    cnpj: item.clientPayment?.cnpj || item.client?.cnpj || "",
  };

  const driverNameFromApi =
    item.driver?.driverName ??
    item.driver?.name ??
    item.freight?.getDriverFreight?.name;

  const freight = item.freight
    ? {
        ...item.freight,
        getDriverFreight: {
          ...item.freight.getDriverFreight,
          ...(driverNameFromApi != null && String(driverNameFromApi).trim() !== ""
            ? { name: driverNameFromApi }
            : {}),
        },
      }
    : {
        valueFreightage: item.valueFreightage ?? item.freightValue ?? 0,
        getDriverFreight: {
          uidDriver: item.driver?.uid,
          name: driverNameFromApi,
        },
      };

  const driver = item.driver || {
    valueNegotiated:
      item.paymentToDriver ??
      item.driverValueNegotiated ??
      item.driver?.valueNegotiated ??
      0,
    valueInitial: item.driver?.valueInitial ?? 0,
    valueFinal: item.driver?.valueFinal ?? 0,
    paymentCondition: item.driver?.paymentCondition,
  };

  const firstDelivery = {
    ...(item.firstDelivery || {
      city: item.origin?.city,
      address: item.origin?.address,
      cep: item.origin?.cep,
      dateColeta: item.origin?.dateColeta || item.dateColeta,
      coordinates: item.origin?.coordinates,
      name: item.origin?.name,
      uf: item.origin?.uf,
    }),
    completed:
      item.firstDelivery?.completed ??
      item.firstDeliveryCompleted ??
      false,
  };

  const lastDelivery = item.lastDelivery || {
    city: item.destination?.city,
    address: item.destination?.address,
    cep: item.destination?.cep,
    coordinates: item.destination?.coordinates,
    name: item.destination?.name,
  };

  const shipper = item.shipper || {
    uid: item.uidShipper || "",
    name: item.shipperName || "",
  };

  const baseStatus = item.status || {};
  const rawDescribe =
    baseStatus.describe ??
    item.statusDescribe ??
    item.status_text ??
    "";
  const describeNorm =
    normalizeFreightStatusDescribe(String(rawDescribe).trim()) ||
    String(rawDescribe).trim();
  const status = { ...baseStatus, describe: describeNorm };

  const activeStatus = item.activeStatus || null;
  const rawStatusFinancial = item.statusFinancial ?? item.financialStatus ?? null;
  const statusFinancial = normalizeStoredFinancialStatusValue(rawStatusFinancial);
  const financialStatus =
    formatFinancialStatusValue(item.financialStatus ?? statusFinancial) ||
    formatFinancialStatusValue(statusFinancial) ||
    null;

  const issueDate =
    item.issueDate ??
    normalizeFreightIssueDate(item.createData, item.history?.initialDate);

  const dueDateFromApi = item.dueDate ?? null;
  const dueDateInstallmentsFromApi = Array.isArray(item.dueDateInstallments)
    ? item.dueDateInstallments
    : [];
  const paymentConditionLabelFromApi = item.paymentConditionLabel ?? null;

  const fallbackDue =
    dueDateFromApi == null
      ? computeDueDateFromPaymentCondition(
          paymentConditionLabelFromApi,
          firstDelivery?.dateColeta,
        )
      : null;

  return {
    id,
    numberSerial: item.numberSerial ?? item.code ?? item.serial,
    clientPayment,
    shipper: {
      uid: shipper.uid || "",
      name: String(shipper.name || "").trim(),
    },
    freight,
    driver,
    firstDelivery,
    lastDelivery,
    status,
    activeStatus,
    issueDate,
    dueDate: dueDateFromApi ?? fallbackDue?.dueDate ?? null,
    dueDateInstallments:
      dueDateInstallmentsFromApi.length > 0
        ? dueDateInstallmentsFromApi
        : fallbackDue?.dueDateInstallments ?? [],
    paymentConditionLabel:
      paymentConditionLabelFromApi ?? fallbackDue?.paymentConditionLabel ?? null,
    financialInstallments: Array.isArray(item.financialInstallments)
      ? item.financialInstallments
      : [],
    statusFinancial,
    financialStatus,
    firstDeliveryCompleted:
      item.firstDeliveryCompleted ?? firstDelivery?.completed ?? false,
    settlementDate: item.settlementDate ?? item.dataBaixa ?? null,
    boletoAttachment: item.boletoAttachment ?? null,
    attachments: Array.isArray(item.attachments) ? item.attachments : [],
    stopping_points: Array.isArray(item.stopping_points)
      ? item.stopping_points
      : [],
  };
}

export function parseFreightV2ListResponse(result, limit, cursorForPage) {
  const payload = result?.data;
  const items = Array.isArray(payload?.data)
    ? payload.data
    : payload?.data?.items || [];
  const pagination = payload?.pagination ?? {};
  const nextCursor = pagination?.nextCursor ?? null;
  const serverHasMore = pagination?.hasMore === true;
  const hasValidNextCursor =
    typeof nextCursor === "string" &&
    nextCursor.trim().length > 0 &&
    nextCursor !== cursorForPage;
  const hasMore = serverHasMore && hasValidNextCursor;
  const rawTotal = pagination?.total;
  const total =
    typeof rawTotal === "number" && Number.isFinite(rawTotal) ? rawTotal : null;
  return {
    items,
    activeStatus: payload?.activeStatus ?? null,
    nextCursor,
    hasMore,
    hasMoreForFetchAll: hasMore,
    total,
  };
}

/**
 * Total exibido no Ant Design Table com paginação por cursor.
 * Usa `total` do servidor quando disponível; caso contrário, calcula o mínimo
 * necessário para navegação sequencial sem inflar o total com pageSize inteiro.
 */
export function resolveCursorTableTotal({
  serverTotal,
  current,
  pageSize,
  pageItemCount,
  hasMore,
}) {
  if (typeof serverTotal === "number" && Number.isFinite(serverTotal)) {
    return Math.max(0, serverTotal);
  }

  const loadedThrough = (current - 1) * pageSize + pageItemCount;
  if (!hasMore) return loadedThrough;

  // Habilita apenas a próxima página sequencial no Ant Design (não estima o dataset).
  return current * pageSize + 1;
}

function shipperIsTransport(shipper) {
  if (!shipper || typeof shipper !== "object") return false;
  const value = shipper.isTransport;
  return (
    value === true ||
    value === 1 ||
    String(value).toLowerCase() === "true"
  );
}

export function isUserTransportadora(user, shipper) {
  return (
    shipperIsTransport(shipper) ||
    user?.Transportadora === true ||
    user?.transportadora === true
  );
}

/**
 * Escopo do GET /freight/v2.
 * - Sempre envia idUser para o backend montar escopo via user_web.
 * - viewALL: viewAll=true + affiliates (sem restringir só ao uidShipper).
 * - Transportadora: uidShipper + isTransportadora.
 */
export function buildFreightV2ScopeParams(user, shipper = null) {
  const params = {};
  const carrierUid = String(user?.uidShipper ?? "").trim();
  const isTransportadora = isUserTransportadora(user, shipper);

  if (isTransportadora && carrierUid) {
    params.uidShipper = carrierUid;
    params.isTransportadora = true;
    if (user?.uid) params.idUser = user.uid;
    return params;
  }

  if (user?.uid) {
    params.idUser = user.uid;
  }

  if (user?.viewALL === true) {
    params.viewAll = "true";
  } else {
    if (typeof user?.viewALL === "boolean") {
      params.viewAll = String(user.viewALL);
    }
    if (user?.uidShipper) {
      params.uidShipper = user.uidShipper;
    }
  }

  if (
    Array.isArray(user?.uidShipperAffiliates) &&
    user.uidShipperAffiliates.length
  ) {
    params.affiliates = user.uidShipperAffiliates.join(",");
  }

  return params;
}

export function buildFreightListScopeParams(user) {
  return buildFreightV2ScopeParams(user);
}
